import { PropsWithChildren, RefObject, useEffect, useRef, useState } from 'react';

import { CompanyInfoResponse } from '~hooks/query/company/useCompanyInfo';
import useIntersectionObserver from '~hooks/useIntersectionObserver';
import useMedia from '~hooks/useMedia';
import throttle from '~utils/lodash/throttle';

import AsideCard from './AsideCard';
import CompanyRelations from './CompanyRelations';
import SalaryReportSection from './SalaryReportSection';
import * as S from './style';

interface Props {
  targetRef: RefObject<HTMLElement>;
  companyInfo?: CompanyInfoResponse;
}

export default function Aside({ companyInfo, targetRef }: PropsWithChildren<Props>) {
  const ref = useRef<HTMLDivElement>(null);
  const contentsRef = useRef<HTMLDivElement>(null);
  const [isActiveScrollEvent, setIsActiveScrollEvent] = useState<boolean>();
  const [fixedStickyTop, setFixedStickyTop] = useState<number>(0);
  const { isMobile, isDesktop, isTablet, isReady } = useMedia();

  const entry = useIntersectionObserver(ref, {
    root: null,
    rootMargin: '-138px 0px 300px',
    threshold: 1,
  });

  useEffect(() => {
    const gapFromManageSection = 233;
    const fixedContentsOffsetTop = 135;
    const topArea = 280;

    const handleScroll = () => {
      if (targetRef.current && contentsRef.current) {
        const footerOffsetTop = targetRef.current.offsetTop;
        const contentsHeight = contentsRef.current?.offsetHeight;
        const top = footerOffsetTop - gapFromManageSection - contentsHeight - topArea;
        const triggerPoint =
          window.scrollY + contentsHeight + fixedContentsOffsetTop + gapFromManageSection;

        if (footerOffsetTop < triggerPoint) {
          setFixedStickyTop(top);
        } else {
          setFixedStickyTop(0);
        }
      }
    };

    if (!isMobile && !isActiveScrollEvent) {
      window.addEventListener('scroll', throttle(handleScroll, 100));
      setIsActiveScrollEvent(true);

      setTimeout(() => {
        handleScroll();
      }, 800);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, [targetRef, isMobile, isActiveScrollEvent]);

  return (
    <S.Wrapper ref={ref}>
      <S.Contents
        ref={contentsRef}
        isSticky={!entry?.isIntersecting}
        fixedStickyTop={fixedStickyTop}>
        <AsideCard noPadding>
          <SalaryReportSection
            companyName={companyInfo?.name}
            PK_NM_HASH={companyInfo?.PK_NM_HASH}
          />
        </AsideCard>
        <AsideCard>
          <CompanyRelations />
        </AsideCard>

        <a
          data-attribute-id='company__banner__click'
          data-banner-location='right'
          data-banner-type='ads'>
          {isMobile && <S.GoogleAdsBanner bannerType='mobileBanner' slotId='8076432031' />}
          {isTablet && <S.GoogleAdsBanner bannerType='leaderBoard' slotId='8397610632' />}
          {isDesktop && <S.GoogleAdsBanner bannerType='mediumRectangle' slotId='3872914400' />}
        </a>
      </S.Contents>
    </S.Wrapper>
  );
}
